/**
 * Escalations — list of open Creative Memory escalations (cheap_eval
 * boundary-case proposals routed to ESCALATE).
 *
 * Backed by GET /api/v1/creative-memory/escalations and POST
 * /api/v1/creative-memory/escalations/{record_id}/resolve.
 */

import { useEffect, useState } from 'react';
import { AlertTriangle, RefreshCw, CheckCircle2 } from 'lucide-react';
import { clsx } from 'clsx';
import { getApiBase } from '@/services/api';

interface EscalationRecord {
  record_id: string;
  playbook_id: string;
  proposed_rule_name?: string;
  rationale?: string;
  ts?: string;
  creative_id?: string;
  persona_id?: string;
  proposed_by?: string;
}

export function EscalationsPage() {
  const [items, setItems] = useState<EscalationRecord[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [resolving, setResolving] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await fetch(
        `${getApiBase()}/api/v1/creative-memory/escalations?limit=200`,
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      const data = await resp.json();
      setItems(data.items || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const resolve = async (recordId: string) => {
    setResolving(recordId);
    try {
      const resp = await fetch(
        `${getApiBase()}/api/v1/creative-memory/escalations/${encodeURIComponent(
          recordId,
        )}/resolve`,
        {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ resolved_by: 'admin_ui' }),
        },
      );
      if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
      setItems((cur) => cur.filter((it) => it.record_id !== recordId));
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setResolving(null);
    }
  };

  return (
    <div className="px-6 py-6">
      <header className="mb-5 flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-5 h-5 text-rose-500" />
            <h1 className="text-xl font-semibold text-slate-800">
              Escalations
            </h1>
          </div>
          <p className="mt-1 text-sm text-slate-600 max-w-3xl">
            Rule proposals the garden cycle's cheap_eval pass routed to
            ESCALATE — usually because they sit at the confidence boundary
            and need a human call. Resolving an escalation marks it
            handled; the next cycle won't re-surface it.
          </p>
        </div>
        <button
          type="button"
          onClick={() => load()}
          disabled={loading}
          className="flex items-center gap-1 rounded-md border border-slate-200 bg-white px-3 py-1.5 text-xs text-slate-700 hover:border-slate-300 disabled:opacity-50"
        >
          <RefreshCw className={clsx('w-3 h-3', loading && 'animate-spin')} />
          Refresh
        </button>
      </header>

      {error && (
        <div className="mb-5 rounded-md border border-rose-200 bg-rose-50 px-3 py-2 text-xs text-rose-800">
          {error}
        </div>
      )}

      {!error && !loading && items.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-200 bg-white p-12 text-center">
          <CheckCircle2 className="mx-auto w-8 h-8 text-emerald-400" />
          <p className="mt-3 text-sm font-medium text-slate-700">
            No open escalations
          </p>
          <p className="mt-1 text-xs text-slate-500">
            All cheap_eval boundary cases have been resolved.
          </p>
        </div>
      )}

      {items.length > 0 && (
        <ul className="space-y-2">
          {items.map((it) => (
            <li
              key={it.record_id}
              className="rounded-lg border border-slate-200 bg-white px-4 py-3"
            >
              <header className="flex flex-wrap items-baseline gap-x-2 gap-y-1">
                <span className="text-xs font-semibold text-slate-800">
                  {it.proposed_rule_name || it.record_id}
                </span>
                <span className="rounded bg-amber-50 px-1.5 py-0.5 text-[10px] font-mono text-amber-700">
                  pb {it.playbook_id}
                </span>
                {it.creative_id && (
                  <span className="rounded bg-sky-50 px-1.5 py-0.5 text-[10px] font-mono text-sky-700">
                    creative {it.creative_id}
                  </span>
                )}
                <span className="ml-auto text-[10px] text-slate-400">
                  {it.ts ? new Date(it.ts).toLocaleString() : ''}
                </span>
              </header>
              {it.rationale && (
                <p className="mt-1 text-[12px] text-slate-600 leading-snug">
                  {it.rationale}
                </p>
              )}
              <div className="mt-2 flex justify-end">
                <button
                  type="button"
                  onClick={() => resolve(it.record_id)}
                  disabled={resolving === it.record_id}
                  className="rounded-md border border-emerald-200 bg-emerald-50 px-2.5 py-1 text-[11px] font-medium text-emerald-800 hover:bg-emerald-100 disabled:opacity-50"
                >
                  {resolving === it.record_id ? 'Resolving…' : 'Resolve'}
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
